import React, { createContext, useState, useEffect, useContext, useMemo, useCallback } from 'react';
import { useCookies } from 'react-cookie';
import * as Sentry from '@sentry/react';
import { useLazyQuery } from '@apollo/client';
import { useLocation } from 'react-router-dom';
import {
  getUserId,
  removeToken,
  setToken,
  refreshToken,
  formatFirstName,
  hasValidStoredToken,
} from '../utils';
import { GET_USER_INFO } from '../services/ApolloClient';
import { getUserHash } from '../services/API/UserAPI';

type MeUser = {
  email?: string | null;
  firstName?: string | null;
  lastName?: string | null;
};

interface AuthContextData {
  signed: boolean | null;
  userInfo: MeUser | null;

  login(token: string): void;
  logout(): void;
  cleanAuthCookie(): void;
}

const AuthContext = createContext<AuthContextData>({} as AuthContextData);

export const AuthProvider: React.FC = ({ children }) => {
  const [signed, setSigned] = useState<boolean | null>(null);
  const [cookies] = useCookies(['em_logged_in', 'em_subscription_platform']);
  const location = useLocation();

  const [userInfo, setUserInfo] = useState<MeUser | null>(null);

  const [getUserInfo] = useLazyQuery(GET_USER_INFO, {
    fetchPolicy: 'no-cache',
  });

  const logout = useCallback(() => {
    window.analytics?.reset();
    window.location.href = `${window.location.origin}/logout/`;
  }, []);

  const cleanAuthCookie = useCallback(() => {
    removeToken();
    setSigned(false);
    setUserInfo(null);
  }, []);

  const login = useCallback((token: string) => {
    setToken(token);
  }, []);

  const refreshUserInfo = useCallback(async (): Promise<MeUser | null> => {
    try {
      const res = await getUserInfo();
      const me = (res?.data?.me ?? null) as MeUser | null;
      setUserInfo(me);
      return me;
    } catch (e) {
      console.log(e);
      setUserInfo(null);
      return null;
    }
  }, [getUserInfo]);

  const updateIntercomSettings = useCallback(
    async (id: string, platform: string, email: string) => {
      const accessType = platform !== 'none' ? 'all access' : 'education';
      const hashData = await getUserHash();

      window.Intercom('update', {
        user_id: id,
        email,
        user_hash: hashData?.user_hash ?? '',
        'access-type': accessType,
      });

      if (window.clarity) {
        window.clarity('set', 'internalId', id);
      }
    },
    [],
  );

  useEffect(() => {
    const hasValidSession = cookies?.em_logged_in === '1' && hasValidStoredToken();

    if (hasValidSession) {
      setSigned(true);

      const id = getUserId();
      if (id) {
        Sentry.setUser({ id });
      }

      if (!userInfo) {
        refreshUserInfo();
      }
    } else {
      setSigned(false);
      removeToken();
      setUserInfo(null);
      Sentry.setUser(null);
    }
  }, [cookies?.em_logged_in, refreshUserInfo, userInfo]);

  useEffect(() => {
    if (cookies?.em_logged_in !== '1') return;

    const id = getUserId();
    const email = userInfo?.email ?? null;
    const rawFirstName = userInfo?.firstName ?? null;

    if (!id || !email) return;

    const traits: Record<string, string> = { email };

    if (rawFirstName && rawFirstName.trim()) {
      traits.first_name = formatFirstName(rawFirstName);
    }

    window.analytics?.identify(id, traits);

    updateIntercomSettings(id, cookies?.em_subscription_platform, email);
  }, [
    cookies?.em_logged_in,
    cookies?.em_subscription_platform,
    updateIntercomSettings,
    userInfo?.email,
    userInfo?.firstName,
  ]);

  useEffect(() => {
    (async () => {
      try {
        if (!location.pathname.includes('authenticate-by-link')) {
          await refreshToken();
        }
      } catch (e) {
        console.log(e);
      }
    })();
  }, [location]);

  const value = useMemo(() => {
    return {
      signed,
      userInfo,
      login,
      logout,
      cleanAuthCookie,
    };
  }, [login, logout, signed, cleanAuthCookie, userInfo]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export function useAuth() {
  return useContext(AuthContext);
}
